import { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import type { ResidualClassification } from "../api/cars";
import type { CarSortKey, CarSummary } from "../api/client";
import { heroSrc } from "../api/photo";
import FavouriteStar from "../components/FavouriteStar";

/** Link to a car's detail page, carrying the inventory's `?…` filter with it.
 *
 * The filter primitive lives entirely in the query string (see
 * `utils/filterUrl.ts`), so a bare `/{brand}/cars/{id}` link drops every
 * selection the moment the user opens a car — and CarDetail's "Back to
 * inventory" then has nothing to restore. Threading the search string through
 * the detail URL (rather than through router history state) keeps the round
 * trip working after a refresh, in a new tab, and from a shared link.
 *
 * It's a component, not an inline `<Link>`, so it can call `useLocation()` —
 * `COLUMNS` is a module-level constant and its `cell` callbacks get no props
 * beyond the car.
 */
function CarDetailLink({ car }: { car: CarSummary }) {
  const { search } = useLocation();
  return (
    <Link
      to={{ pathname: `/${car.brand_code}/cars/${car.car_id}`, search }}
      className="font-medium text-blue-600 hover:underline"
    >
      {car.display_name ?? car.model_code ?? "—"}
    </Link>
  );
}

function HeroThumb({ car }: { car: CarSummary }) {
  const primary = heroSrc(car.hero_photo_url, car.hero_photo_sha256);
  const [src, setSrc] = useState<string | null>(primary);

  // If the local file 404s (worker hasn't downloaded yet, ZFS snapshot
  // restore lag, etc.) fall back to the upstream CDN URL.
  const onError = () => {
    if (src && car.hero_photo_url && src !== car.hero_photo_url) {
      setSrc(car.hero_photo_url);
    } else {
      setSrc(null);
    }
  };

  if (!src) {
    return (
      <div
        className="w-14 h-10 sm:w-20 sm:h-14 rounded bg-gray-100"
        aria-hidden="true"
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      onError={onError}
      className="w-14 h-10 sm:w-20 sm:h-14 object-cover rounded bg-gray-100"
    />
  );
}

function StatusBadge({ car }: { car: CarSummary }) {
  if (car.sale_status === "sold") {
    return (
      <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
        Sold
      </span>
    );
  }
  if (car.sale_status === "exported") {
    return (
      <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
        Exported
      </span>
    );
  }
  if (car.status === "disappeared") {
    return (
      <span className="ml-2 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
        Unavailable
      </span>
    );
  }
  return null;
}

export const NEW_ARRIVAL_DAYS = 7;
const NEW_ARRIVAL_MS = NEW_ARRIVAL_DAYS * 24 * 60 * 60 * 1000;

/** True when we first saw the car within the last {@link NEW_ARRIVAL_DAYS} days.
 *
 * `first_seen_at` is when *our scraper* first observed the listing, not when
 * the dealer published it — a car listed before we started covering its brand
 * carries the date of that brand's first scrape, so the badge lights up for
 * the whole catalogue on a brand's first run and settles from then on.
 *
 * Derived client-side from a field the payload already carries (no new server
 * field), and evaluated at render, so a tab left open for a week only cools
 * down on the next fetch — the same posture as the price-cut freshness tint.
 */
export function isNewArrival(firstSeenAt: string | null): boolean {
  if (!firstSeenAt) {
    return false;
  }
  const seen = Date.parse(firstSeenAt);
  return !Number.isNaN(seen) && Date.now() - seen <= NEW_ARRIVAL_MS;
}

// Flags a car that entered the catalogue in the last week. Green, so it never
// reads as the amber/purple "the price moved" family — this is about arrival,
// not about money.
function NewBadge({ car }: { car: CarSummary }) {
  if (!isNewArrival(car.first_seen_at)) {
    return null;
  }
  const seen = new Date(car.first_seen_at).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
  return (
    <span
      className="ml-2 text-xs bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded whitespace-nowrap"
      title={`First seen ${seen} — new in the last ${NEW_ARRIVAL_DAYS} days`}
    >
      New
    </span>
  );
}

const FRESH_CUT_MS = 24 * 60 * 60 * 1000;

/** True when the reduction was observed within the last 24 hours.
 *
 * Recency is measured against `price_cut_at`, which is when *we* saw the cut
 * — with a daily scrape cadence this effectively means "found by the most
 * recent scrape". A clock-skewed or future timestamp reads as fresh rather
 * than stale, which is the harmless direction. Evaluated at render, so a tab
 * left open overnight only cools down on the next fetch.
 */
function isFreshCut(observedAt: string | null): boolean {
  if (!observedAt) {
    return false;
  }
  const seen = Date.parse(observedAt);
  return !Number.isNaN(seen) && Date.now() - seen <= FRESH_CUT_MS;
}

// Flags a car whose asking price is net lower than it was a week ago. The
// server sends the drop only when it's a genuine net fall (§2.4.3), so there's
// no threshold logic here — if the field is set, the badge shows. Purple marks
// a cut seen in the last 24 hours, amber an older one still inside the 7-day
// window. The date is when *we* saw the cut, not necessarily when the dealer
// made it, so the tooltip says "seen".
function PriceCutBadge({ car }: { car: CarSummary }) {
  const cut = car.price_cut_gbp;
  if (cut == null || cut <= 0) {
    return null;
  }
  const fresh = isFreshCut(car.price_cut_at);
  const tone = fresh
    ? "bg-purple-100 text-purple-800"
    : "bg-amber-100 text-amber-800";
  const amount = `£${Math.round(cut).toLocaleString("en-GB")}`;
  const seen = car.price_cut_at
    ? new Date(car.price_cut_at).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      })
    : null;
  const window = fresh ? "in the last 24 hours" : "in the last 7 days";
  return (
    <span
      className={`ml-2 text-xs ${tone} px-1.5 py-0.5 rounded whitespace-nowrap`}
      title={
        seen
          ? `Price cut ${amount} ${window} (seen ${seen})`
          : `Price cut ${amount} ${window}`
      }
    >
      ↓ {amount}
    </span>
  );
}

// "vs Expected" cell: the gap between the asking price and the regression's
// expected price (actual − expected). Null when the car's model has no usable
// regression — render a muted dash.
//
// gck303/evtracker#60: tone follows the same classification the leaderboard
// and detail breakdown use (error-scaled z where the snapshot has one, else
// the percentage-band fallback) rather than a naive sign check, so a car
// barely below expected reads as neutral ("at market") the same way it does
// everywhere else the value verdict is shown. A `no_peers` car (leverage too
// high — its spec is unique enough that the fit reproduces its own price)
// renders the £ figure muted, with a tooltip explaining the band isn't a
// meaningful signal for it.
// Every off-market band gets a background tint — coloured text alone reads as
// the same grey as at_market at a glance — lighter for below/above, stronger
// for the well_* extremes. at_market stays untinted.
const VS_EXPECTED_TONE: Record<ResidualClassification, string> = {
  well_below: "rounded px-1.5 py-0.5 bg-green-200 text-green-900",
  below: "rounded px-1.5 py-0.5 bg-green-50 text-green-700",
  at_market: "text-gray-600",
  above: "rounded px-1.5 py-0.5 bg-pink-50 text-red-600",
  well_above: "rounded px-1.5 py-0.5 bg-pink-200 text-red-800",
};

function PriceDelta({ car }: { car: CarSummary }) {
  const delta = car.price_difference_gbp;
  if (delta == null) {
    return <span className="text-gray-300">—</span>;
  }
  const rounded = Math.round(delta);
  const sign = rounded > 0 ? "+" : rounded < 0 ? "−" : "";
  const expectedText =
    car.expected_price_gbp != null
      ? `expected £${Math.round(car.expected_price_gbp).toLocaleString("en-GB")}`
      : undefined;

  if (car.vs_expected_no_peers) {
    return (
      <span
        className="font-medium text-gray-400"
        title={[expectedText, "No comparable peers — band not meaningful"]
          .filter(Boolean)
          .join(" · ")}
      >
        {sign}£{Math.abs(rounded).toLocaleString("en-GB")}
      </span>
    );
  }

  const tone = car.vs_expected_classification
    ? VS_EXPECTED_TONE[car.vs_expected_classification]
    : "text-gray-600";
  return (
    <span className={`font-medium ${tone}`} title={expectedText}>
      {sign}£{Math.abs(rounded).toLocaleString("en-GB")}
    </span>
  );
}

// Structure week, as Polestar's own field renders it: 202423 -> "2024.23".
// One-week resolution, so no day is implied. Blank for every brand but Polestar
// — no other payload carries the field.
function StructureWeek({ car }: { car: CarSummary }) {
  const sw = car.structure_week;
  if (!sw || sw.length !== 6) {
    return <span className="text-gray-300">—</span>;
  }
  return (
    <span className="text-gray-600 tabular-nums" title={`structureWeek ${sw}`}>
      {sw.slice(0, 4)}.{sw.slice(4)}
    </span>
  );
}

// UK age identifier for the registration date — 24, 74, 25. Derived server-side
// from the date rather than the plate string, so private plates still show a
// cohort. Sits next to the structure week so the two are directly comparable.
function PlateAge({ car }: { car: CarSummary }) {
  const id = car.registration_plate_identifier;
  if (!id) {
    return <span className="text-gray-300">—</span>;
  }
  return (
    <span
      className="text-gray-600 tabular-nums"
      title={
        car.first_registered_at
          ? `First registered ${new Date(car.first_registered_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
          : undefined
      }
    >
      {id}
    </span>
  );
}

// Last six characters of the VIN, verbatim. Server sends them as a string so
// leading zeros survive (000905, not 905) and non-numeric tails still render.
// Gated separately from the structure-week columns: VIN coverage and
// structureWeek coverage are different sets of brands.
function VinSerial({ car }: { car: CarSummary }) {
  if (!car.vin_serial) {
    return <span className="text-gray-300">—</span>;
  }
  return (
    <span className="text-gray-600 tabular-nums font-mono text-xs">
      {car.vin_serial}
    </span>
  );
}

export type ColumnId =
  | "fav"
  | "thumb"
  | "model"
  | "year"
  | "motor"
  | "price"
  | "vs_expected"
  | "mileage"
  | "structure"
  | "reg"
  | "reg_offset"
  | "vin"
  | "location"
  | "drivetrain"
  | "colour"
  | "interior"
  | "wheels"
  | "battery_soh"
  | "first_seen"
  | "last_seen";

export interface ColumnDef {
  id: ColumnId;
  /** Header text; "" for the thumbnail column. */
  label: string;
  /** Responsive visibility classes shared by the th and its td. */
  responsiveCls: string;
  /** Non-responsive part of the td className (padding, tone, wrapping). */
  cellCls: string;
  /** Server-side sort key; absent = header not clickable (spec §2.4.3). */
  sortKey?: CarSortKey;
  /** Shown when the user has no stored preference for the column. */
  defaultVisible: boolean;
  /** Always rendered and not offered in the column chooser (thumb, model). */
  lockedVisible?: boolean;
  /**
   * Column exists only for a signed-in user (the favourites star). Unlike
   * `dataGate` this cannot be decided from the page's rows — auth is session
   * state — so `Inventory` ANDs it in alongside the data gate.
   */
  requiresAuth?: boolean;
  /** Initial width (px) under table-fixed layout; user-resizable. */
  defaultWidth: number;
  /** Drag-resize floor (px). */
  minWidth: number;
  /**
   * Column renders only when the current page's data warrants it (e.g.
   * structure week is Polestar-only). Absent = always available. Drives
   * visibility off the data rather than a brand check, so a column appears
   * by itself if another brand starts supplying the field.
   */
  dataGate?: (cars: CarSummary[]) => boolean;
  cell: (car: CarSummary) => ReactNode;
}

const hasStructure = (cars: CarSummary[]) =>
  cars.some((c) => c.structure_week != null);
const hasVin = (cars: CarSummary[]) => cars.some((c) => c.vin_serial != null);
const hasRegOffset = (cars: CarSummary[]) =>
  cars.some((c) => c.registration_offset_days != null);
const hasSoh = (cars: CarSummary[]) =>
  cars.some((c) => c.battery_soh_value != null);

const shortDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

// Single source of truth for the inventory table: header row, body cells and
// the empty-state colSpan all derive from this array (display order).
export const COLUMNS: readonly ColumnDef[] = [
  {
    id: "fav",
    // Wide enough for the 30px star plus its left padding — a narrower column
    // clips it under the table's fixed layout.
    defaultWidth: 48,
    minWidth: 44,
    defaultVisible: true,
    lockedVisible: true,
    requiresAuth: true,
    label: "",
    responsiveCls: "",
    cellCls: "pl-2 sm:pl-3 pr-0 py-3",
    cell: (car) => <FavouriteStar carId={car.car_id} initial={car.is_favourite} />,
  },
  {
    id: "thumb",
    defaultWidth: 96,
    minWidth: 56,
    defaultVisible: true,
    lockedVisible: true,
    label: "",
    responsiveCls: "",
    cellCls: "px-2 sm:px-4 py-3",
    cell: (car) => <HeroThumb car={car} />,
  },
  {
    id: "model",
    defaultWidth: 240,
    minWidth: 120,
    defaultVisible: true,
    lockedVisible: true,
    sortKey: "model",
    label: "Model",
    responsiveCls: "",
    cellCls: "px-2 sm:px-4 py-3",
    cell: (car) => (
      <>
        <CarDetailLink car={car} />
        <StatusBadge car={car} />
        <NewBadge car={car} />
        <PriceCutBadge car={car} />
      </>
    ),
  },
  {
    id: "year",
    defaultWidth: 70,
    minWidth: 60,
    defaultVisible: true,
    sortKey: "year",
    label: "Year",
    responsiveCls: "hidden sm:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm text-gray-600",
    cell: (car) => car.model_year ?? "—",
  },
  {
    id: "motor",
    defaultWidth: 190,
    minWidth: 80,
    defaultVisible: true,
    sortKey: "motor",
    label: "Motor",
    responsiveCls: "hidden sm:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm text-gray-600",
    cell: (car) => car.motor_label ?? "—",
  },
  {
    id: "price",
    defaultWidth: 100,
    minWidth: 70,
    defaultVisible: true,
    sortKey: "price",
    label: "Price",
    responsiveCls: "",
    cellCls: "px-2 sm:px-4 py-3 text-sm font-medium whitespace-nowrap",
    cell: (car) =>
      car.price_retail_gbp != null
        ? `£${car.price_retail_gbp.toLocaleString("en-GB")}`
        : "—",
  },
  {
    id: "vs_expected",
    defaultWidth: 115,
    minWidth: 70,
    defaultVisible: true,
    sortKey: "vs_expected",
    label: "vs Expected",
    responsiveCls: "",
    cellCls: "px-2 sm:px-4 py-3 text-sm whitespace-nowrap",
    cell: (car) => <PriceDelta car={car} />,
  },
  {
    id: "mileage",
    defaultWidth: 110,
    minWidth: 70,
    defaultVisible: true,
    sortKey: "mileage",
    label: "Mileage",
    responsiveCls: "",
    cellCls: "px-2 sm:px-4 py-3 text-sm text-gray-600 whitespace-nowrap",
    cell: (car) =>
      car.mileage_distance != null
        ? `${car.mileage_distance.toLocaleString("en-GB")} ${car.mileage_metric ?? "mi"}`
        : "—",
  },
  {
    id: "structure",
    defaultWidth: 95,
    minWidth: 60,
    defaultVisible: true,
    sortKey: "structure",
    label: "Structure",
    responsiveCls: "hidden sm:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm whitespace-nowrap",
    dataGate: hasStructure,
    cell: (car) => <StructureWeek car={car} />,
  },
  {
    id: "reg",
    defaultWidth: 70,
    minWidth: 60,
    defaultVisible: true,
    sortKey: "reg",
    label: "Reg",
    responsiveCls: "hidden sm:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm whitespace-nowrap",
    dataGate: hasStructure,
    cell: (car) => <PlateAge car={car} />,
  },
  {
    id: "vin",
    defaultWidth: 100,
    minWidth: 60,
    defaultVisible: true,
    sortKey: "vin_serial",
    label: "VIN last 6",
    responsiveCls: "hidden md:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm whitespace-nowrap",
    dataGate: hasVin,
    cell: (car) => <VinSerial car={car} />,
  },
  {
    id: "location",
    defaultWidth: 130,
    minWidth: 60,
    defaultVisible: true,
    sortKey: "location",
    label: "Location",
    responsiveCls: "hidden md:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm text-gray-600",
    cell: (car) => car.handover_city ?? "—",
  },
  // ------------------------------------------------------------------
  // Chooser-only columns: hidden by default, enabled per device via the
  // Columns popover (spec §2.4.3, amended 2026-08-08).
  // ------------------------------------------------------------------
  {
    id: "drivetrain",
    defaultWidth: 105,
    minWidth: 60,
    defaultVisible: false,
    sortKey: "drivetrain",
    label: "Drivetrain",
    responsiveCls: "hidden sm:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm text-gray-600",
    cell: (car) => car.drivetrain ?? "—",
  },
  {
    id: "colour",
    defaultWidth: 140,
    minWidth: 60,
    defaultVisible: false,
    label: "Colour",
    responsiveCls: "hidden md:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm text-gray-600",
    cell: (car) => car.exterior_label ?? "—",
  },
  {
    id: "interior",
    defaultWidth: 150,
    minWidth: 60,
    defaultVisible: false,
    label: "Interior",
    responsiveCls: "hidden md:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm text-gray-600",
    cell: (car) => car.interior_label ?? "—",
  },
  {
    id: "wheels",
    defaultWidth: 150,
    minWidth: 60,
    defaultVisible: false,
    label: "Wheels",
    responsiveCls: "hidden md:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm text-gray-600",
    cell: (car) => car.wheels_label ?? "—",
  },
  {
    id: "battery_soh",
    defaultWidth: 110,
    minWidth: 60,
    defaultVisible: false,
    sortKey: "battery_soh",
    label: "Battery SoH",
    responsiveCls: "hidden sm:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm text-gray-600 whitespace-nowrap",
    dataGate: hasSoh,
    cell: (car) =>
      car.battery_soh_value != null ? `${car.battery_soh_value}%` : "—",
  },
  {
    id: "reg_offset",
    defaultWidth: 100,
    minWidth: 60,
    defaultVisible: false,
    label: "Reg offset",
    responsiveCls: "hidden md:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm text-gray-600 whitespace-nowrap",
    dataGate: hasRegOffset,
    // Positive is normal (built, then registered); negative is real and
    // deliberately not clamped. Not sortable: the value is Python-derived
    // at the API boundary with malformed-input guards (spec §2.4.3).
    cell: (car) =>
      car.registration_offset_days != null
        ? `${car.registration_offset_days} d`
        : "—",
  },
  {
    id: "first_seen",
    defaultWidth: 115,
    minWidth: 60,
    defaultVisible: false,
    sortKey: "first_seen",
    label: "Listed",
    responsiveCls: "hidden md:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm text-gray-600 whitespace-nowrap",
    cell: (car) => shortDate(car.first_seen_at),
  },
  {
    id: "last_seen",
    defaultWidth: 115,
    minWidth: 60,
    defaultVisible: false,
    sortKey: "last_seen",
    label: "Last seen",
    responsiveCls: "hidden md:table-cell",
    cellCls: "px-2 sm:px-4 py-3 text-sm text-gray-600 whitespace-nowrap",
    cell: (car) => shortDate(car.last_seen_at),
  },
];

// ---------------------------------------------------------------------------
// Column-visibility preferences (localStorage; sparse override map so columns
// added in future releases pick up their defaultVisible automatically).
// ---------------------------------------------------------------------------

export type ColumnPrefs = Partial<Record<ColumnId, boolean>>;

const COLUMN_IDS: ReadonlySet<string> = new Set(COLUMNS.map((c) => c.id));

/** Drop unknown ids and non-boolean values from a stored preference map. */
export function sanitizeColumnPrefs(raw: unknown): ColumnPrefs | undefined {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return undefined;
  }
  const out: ColumnPrefs = {};
  for (const [key, value] of Object.entries(raw)) {
    if (COLUMN_IDS.has(key) && typeof value === "boolean") {
      out[key as ColumnId] = value;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Column widths (localStorage; sparse px map — only user-adjusted columns are
// stored, so unadjusted ones track their defaultWidth across releases).
// ---------------------------------------------------------------------------

export type ColumnWidths = Partial<Record<ColumnId, number>>;

export const COLUMN_WIDTH_MAX = 600;

const MIN_WIDTH_BY_ID = new Map<string, number>(
  COLUMNS.map((c) => [c.id, c.minWidth]),
);

/** Drop unknown ids and non-numeric values; clamp to [minWidth, max]. */
export function sanitizeColumnWidths(raw: unknown): ColumnWidths | undefined {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return undefined;
  }
  const out: ColumnWidths = {};
  for (const [key, value] of Object.entries(raw)) {
    const min = MIN_WIDTH_BY_ID.get(key);
    if (min !== undefined && typeof value === "number" && !Number.isNaN(value)) {
      out[key as ColumnId] = Math.max(min, Math.min(COLUMN_WIDTH_MAX, value));
    }
  }
  return out;
}
